import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '@flatfile/baseline'

import { useCallback, useEffect, useRef, useState } from 'react'
import { AppHTTPService } from '../../api/http-service'
import { logger } from '../../utils/logging'

export async function getEnvironmentSecrets(environmentId: string) {
  try {
    return (await AppHTTPService.client.secrets.list({ environmentId })).data
  } catch (err) {
    logger.error('Error fetching getEnvironmentSecrets: ', { error: err })
    return []
  }
}

export const addNewSecretToEnvironment = async (secret: {
  name: string
  value: string
  environmentId: string
  id: string
}) => {
  try {
    const { name, value, environmentId } = secret
    if (!name || !value) throw new Error('Name and value are required')
    const { data } = await AppHTTPService.client.secrets.upsert({
      name,
      value,
      environmentId,
    })
    return { success: true, data }
  } catch (err) {
    logger.error('Error adding new env secret: ', { error: err })
    return { success: false, error: err || 'An error occurred while adding the secret' }
  }
}

export const updateSecretInEnvironment = async (secret: {
  name: string
  value: string
  environmentId: string
  id: string
}) => {
  try {
    const secrets = await AppHTTPService.client.secrets.list({ environmentId: secret.environmentId })
    const secretExists = secrets.data.filter((s: any) => s.id === secret.id).length > 0
    if (secretExists) await AppHTTPService.client.secrets.delete(secret.id)
    const { name, value, environmentId } = secret
    const { data } = await AppHTTPService.client.secrets.upsert({
      name,
      value,
      environmentId,
    })

    return { success: true, data }
  } catch (err) {
    logger.error('Error updating secrets in env: ', { error: err })
    return { success: false, error: err || 'An error occurred while updating the secret' }
  }
}

export const removeSecretFromEnvironment = async (secretId: string) => {
  try {
    const response = await AppHTTPService.client.secrets.delete(secretId)
    return response.data
  } catch (err) {
    logger.error('Error deleting secrets in env: ', { error: err })
  }
}

export function EnvironmentSecrets(props: { environment: any; secrets?: any[] }) {
  const { environment, secrets: initialSecrets } = props
  const [secretsList, setSecretsList] = useState<any>(initialSecrets || [])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [formValues, setFormValues] = useState<{ name: string; value: string }>({
    name: '',
    value: '',
  })
  const [formErrors, setFormErrors] = useState<{ name: string | null; value: string | null }>({
    name: null,
    value: null,
  })

  // Update secrets list when initialSecrets changes
  useEffect(() => {
    if (initialSecrets) {
      setSecretsList(initialSecrets)
    }
  }, [initialSecrets])

  const setFieldValue = (field: 'name' | 'value', value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  const setFieldError = (field: 'name' | 'value', error: string | null) => {
    setFormErrors((prev) => ({ ...prev, [field]: error }))
  }

  const resetForm = () => {
    setFormValues({ name: '', value: '' })
    setFormErrors({ name: null, value: null })
  }

  const validateForm = (): boolean => {
    let isValid = true
    const newErrors = { name: null, value: null } as { name: string | null; value: string | null }

    // Validate name field
    if (!formValues.name.trim()) {
      newErrors.name = 'Please enter a name'
      isValid = false
    } else if (secretsList?.filter((secret: any) => secret.name === formValues.name).length > 0) {
      newErrors.name = 'Secret name already exists, please use a new name or edit the secret directly'
      isValid = false
    }

    // Validate value field
    if (!formValues.value.trim()) {
      newErrors.value = 'Please enter a value'
      isValid = false
    }

    setFormErrors(newErrors)
    return isValid
  }

  const updateSecretsList = useCallback((newSecrets: any[]) => {
    setSecretsList(newSecrets)
  }, [])

  const newSecretNameFieldRef = useRef<HTMLInputElement>(null)
  const newSecretValueFieldRef = useRef<HTMLInputElement>(null)

  let newSecretsSubmitted = 0
  const submitNewSecret = (values: { name: string; value: string }) => {
    try {
      if (!validateForm()) throw new Error('Invalid secret name or value')
      const { name, value } = values
      if (secretsList.filter((secret: any) => secret.name === name).length > 0) {
        setFieldError('name', 'Secret name already exists, please use a new name or edit the secret directly')
        throw new Error('Secret name already exists, please use a new name or edit the secret directly')
      }
      if (!name || !value) {
        if (!name) {
          setFieldError('name', 'Please enter a valid string')
        }
        if (!value) {
          setFieldError('value', 'Please enter a valid string')
        }
      }

      addNewSecretToEnvironment({
        name,
        value,
        environmentId: environment.id,
        id: `newSecret${newSecretsSubmitted++}`,
      }).then((result: any) => {
        if (result.success) {
          updateSecretsList(result.data)
          resetForm()
          setErrorMessage(null)
          if (!newSecretNameFieldRef.current || !newSecretValueFieldRef.current) throw new Error('No ref found')
          newSecretNameFieldRef.current.value = ''
          newSecretValueFieldRef.current.value = ''
        } else {
          setErrorMessage(result.error.body.message)
        }
      })
    } catch (e: any) {
      setFieldError('name', `${e}`)
      logger.error('error adding secret', { error: e })
    }
  }

  const submitUpdateSecret = (secret: any) => {
    try {
      updateSecretInEnvironment(secret).then((result: any) => {
        if (result.success) {
          updateSecretsList(result.data)
          setErrorMessage(null)
        } else {
          setErrorMessage(result.error.body.message)
        }
      })
    } catch (e: any) {
      logger.error('error in submitUpdateSecret', { error: e })
    }
  }

  const submitDeleteSecret = (secretId: string) => {
    removeSecretFromEnvironment(secretId).then(() => {
      const newSecrets = secretsList.filter((secret: any) => secret.id !== secretId)
      updateSecretsList(newSecrets)
    })
  }

  const [updateSecretInEnvironmentValues, setUpdatedSecretValues] = useState<any>({
    name: '',
    value: '',
    environmentId: environment.id,
    id: '',
  })
  const [valueIsVisible, updateValueIsVisible] = useState<{ status: boolean; id: string }>({
    status: false,
    id: '',
  })

  const [secretIsBeingEdited, updateSecretIsBeingEdited] = useState<boolean>(false)

  const saveAndExitEditMode = useCallback(() => {
    submitUpdateSecret(updateSecretInEnvironmentValues)
    updateSecretIsBeingEdited(false)
    setUpdatedSecretValues({
      name: '',
      value: '',
      environmentId: environment.id,
      id: '',
    })
  }, [updateSecretInEnvironmentValues, environment.id])

  const nameInputRef = useRef<HTMLInputElement>(null)
  const valueInputRef = useRef<HTMLInputElement>(null)

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget !== valueInputRef.current) {
      saveAndExitEditMode()
    }
  }

  const handleValueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget !== nameInputRef.current) {
      saveAndExitEditMode()
    }
  }

  // If no secrets are provided, return null
  if (!secretsList) return null

  const secretsJSX = secretsList.map((secret: any) => {
    const { name, value, id } = secret
    const isBeingEdited = secretIsBeingEdited && secret.id === updateSecretInEnvironmentValues.id
    const isValueVisible = valueIsVisible.status && valueIsVisible.id === id
    return (
      <TableRow key={secret.id}>
        <TableCell>
          {isBeingEdited ? (
            <Input
              ref={nameInputRef}
              defaultValue={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUpdatedSecretValues({
                  ...updateSecretInEnvironmentValues,
                  name: e.target.value,
                })
              }}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && saveAndExitEditMode()}
              onBlur={handleNameBlur}
            />
          ) : (
            <Text className='ml-3 text-sm'>{name}</Text>
          )}
        </TableCell>
        <TableCell>
          {isBeingEdited ? (
            <Input
              ref={valueInputRef}
              defaultValue={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUpdatedSecretValues({ ...updateSecretInEnvironmentValues, value: e.target.value })
              }}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && saveAndExitEditMode()}
              onBlur={handleValueBlur}
            />
          ) : (
            <div className='relative'>
              <Input disabled type={isValueVisible ? 'text' : 'password'} defaultValue={value} className='pr-10' />
              <div className='absolute right-2 top-1/2 transform -translate-y-1/2'>
                <Button
                  size='icon-sm'
                  variant='ghost'
                  onClick={() => {
                    updateValueIsVisible({
                      status: !isValueVisible,
                      id,
                    })
                  }}
                  icon={isValueVisible ? 'eye-slash' : 'eye-open'}
                />
              </div>
            </div>
          )}
        </TableCell>
        <TableCell className='flex flex-row nowrap'>
          {isBeingEdited ? (
            <>
              <Button size='icon-sm' variant='ghost' onClick={saveAndExitEditMode} icon='check' />
              <Button
                size='icon-sm'
                variant='ghost'
                onClick={() => {
                  updateSecretIsBeingEdited(false)
                  setUpdatedSecretValues({
                    name: '',
                    value: '',
                    environmentId: environment.id,
                    id: '',
                  })
                }}
                icon='cross-small'
              />
            </>
          ) : (
            <>
              <Button
                size='icon-md'
                variant='ghost'
                onClick={() => {
                  if (updateSecretInEnvironmentValues.id !== '' && updateSecretInEnvironmentValues.id !== id)
                    if (confirm('Please finish editing the current secret before editing another secret')) {
                      setUpdatedSecretValues(secret)
                      updateSecretIsBeingEdited(true)
                    } else {
                      return
                    }
                  setUpdatedSecretValues(secret)
                  updateSecretIsBeingEdited(true)
                }}
                icon='pencil'
              />
              <Button
                size='icon-md'
                variant='ghost'
                onClick={() => {
                  submitDeleteSecret(id)
                }}
                icon='trash-can-2'
              />
            </>
          )}
        </TableCell>
      </TableRow>
    )
  })

  return (
    <div className='grid grid-cols-12 m-6 items-center'>
      <div className='col-span-4 pr-6'>
        <h3 className='text-lg font-medium'>Secrets</h3>
        <Text className='text-text-default mt-2 text-sm'>
          Set secrets for this environment that can be accessed inside deployed listeners. Securely encrypted and
          accessible only to credentials with environment level control.
        </Text>
      </div>
      <div className='col-span-7 col-start-6'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className='w-[45%]'>Value</TableHead>
              <TableHead className='w-[15%]'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {secretsJSX}
            <TableRow>
              <TableCell className='align-top'>
                <div className='space-y-2'>
                  <Input
                    size='small'
                    id='secret-name'
                    ref={newSecretNameFieldRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') submitNewSecret(formValues)
                    }}
                    placeholder='Secret Name'
                  />
                  {formErrors.name && <Text className='text-destructive text-xs'>{formErrors.name}</Text>}
                </div>
              </TableCell>
              <TableCell className='align-top'>
                <div className='space-y-2'>
                  <Input
                    size='small'
                    id='secret-value'
                    ref={newSecretValueFieldRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('value', e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') submitNewSecret(formValues)
                    }}
                    placeholder='Secret Value'
                  />
                  {formErrors.value && <Text className='text-destructive text-xs'>{formErrors.value}</Text>}
                </div>
              </TableCell>
              <TableCell className='align-middle'>
                <Button
                  size='icon-md'
                  variant='ghost'
                  onClick={() => {
                    const isInvalid = !validateForm()
                    if (isInvalid) return
                    setFormErrors({ name: null, value: null })
                    submitNewSecret(formValues)
                  }}
                  icon='plus-small'
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {errorMessage && (
          <Alert variant='destructive' className='mt-4'>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
}
